import React, { useEffect, useState } from 'react';
import { FoldersApi } from '../../../api/folders/folders';
import CustomConfirmationDialog from '../../../components/CustomConfirmationDialog/CustomConfirmationDialog';
import CopyItemForm from '../../../components/Forms/CopyItemForm';
import { useSnackbar } from '../../../data/SnackbarContext';
import { ITEM_COPY_FORM, SNACKBAR_MESSAGES } from '../../../data/StringConstants';

/**
 * 
 * @param {object} props Component Props
 * @param {function} props.stateDelegate Function to return a copy payload to for purposes of updating callee's state, view, etc..
 * @param {boolean} props.open Used to determine if the Dialog should be displayed
 * @param {function} props.toggle Function used to manipulate the `open` value
 * @param {object} props.item Item to copy
 * @param {number} props.item.Id Id of the Item to copy from
 * @param {string} props.item.Name Name of the Item to copy from
 * @param {string[guid]} props.item.PromotionContextId Guid of the "Promotion" aka Folder of item origin
 * 
 */
export default function CopyItemDialogContainer({ item, stateDelegate, open, toggle }) {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const [copyData, setCopyData] = useState({
    folders: [],
  })

  /**
   * @description retrieves available folder
   * @param {boolean} open - represents whether or not the Copy Item Dialog is to be openned
   * @param {boolean} isCancelled - represents whether or not the request / render has been aborted
   */
  const establishFolders = async (open, isCancelled) => {
    const availableFolders = await FoldersApi.fetchAllActiveFoldersForAnOrg();
    setCopyData({ folders: availableFolders.data })
  }

  useEffect(() => {
    let isCancelled = false;
    if (open && !isCancelled) {
      establishFolders(open, isCancelled);
    }

    return () => (isCancelled = true);
  }, [open])


  /**
   * @description Performs Item Copy, messages the result to the `stateDelegate`
   * then closes the dialog
   * @param {object} payload - Form data defining Copy action
   */
  const saveCopy = async payload => {
    try {
      const result = await FoldersApi.copyItemToFolder({
        id: item.Id,
        name: payload.name,
        newContextId: payload.targetFolder
      });

      if (result.status === 200) {
        result.data.folderId = copyData.folders
          .filter(f => f.CorrelationId === payload.targetFolder)
          .map(f => f.Id).pop();
        close()
        stateDelegate(result.data)
        showSuccessSnackbar(SNACKBAR_MESSAGES.COPY_ITEM_SUCCESS);
      }
    } catch (error) {
      showErrorSnackbar(SNACKBAR_MESSAGES.COPY_ITEM_ERROR);
    }
  }

  /**
   * @description Clears managed state and closes the dialog 
   */
  const close = () => {
    setCopyData({ folders: [] })
    toggle()
  }

  return (
    <CustomConfirmationDialog
      onClose={close}
      open={!!copyData.folders.length}
      title={ITEM_COPY_FORM.TITLE}
      fullWidth
    >
      <CopyItemForm
        data={{
          item,
          folders: copyData.folders,
        }}
        onCancel={close}
        onSubmit={saveCopy}
      />
    </CustomConfirmationDialog>
  )
}